import { useEffect, useState } from 'react'
import AnimationLayout from './components/AnimationLayout'

interface Badge {
  name: string
  emoji: string
  description: string
  points_awarded: number
}

interface Props {
  animation: { id: number; name: string; description: string }
  badge: Badge
  all_animations: { id: number; name: string }[]
}

interface Particle {
  id: number
  angle: number
  distance: number
  size: number
  speed: number
}

export default function Animation23({ animation, badge, all_animations }: Props) {
  const [showBadge, setShowBadge] = useState(false)
  const [particles, setParticles] = useState<Particle[]>([])

  useEffect(() => {
    // Créer les particules du vortex
    const newParticles: Particle[] = []
    for (let i = 0; i < 40; i++) {
      newParticles.push({
        id: i,
        angle: Math.random() * 360,
        distance: 80 + Math.random() * 100,
        size: 4 + Math.random() * 8,
        speed: 2 + Math.random() * 3
      })
    }
    setParticles(newParticles)

    setTimeout(() => setShowBadge(true), 800)
  }, [])

  return (
    <AnimationLayout animation={animation} allAnimations={all_animations}>
      <style>{`
        @keyframes vortexSpin {
          0% {
            transform: rotate(var(--start-angle)) translateX(var(--distance)) scale(1);
            opacity: 1;
          }
          50% {
            transform: rotate(calc(var(--start-angle) + 180deg)) translateX(calc(var(--distance) * 0.5)) scale(0.8);
            opacity: 0.8;
          }
          100% {
            transform: rotate(calc(var(--start-angle) + 360deg)) translateX(var(--distance)) scale(1);
            opacity: 1;
          }
        }
        @keyframes badgeAppear {
          0% {
            transform: scale(0) rotate(-180deg);
            opacity: 0;
          }
          60% {
            transform: scale(1.2) rotate(10deg);
          }
          100% {
            transform: scale(1) rotate(0deg);
            opacity: 1;
          }
        }
        .vortex-particle {
          animation: vortexSpin var(--speed) linear infinite;
        }
        .badge-appear {
          animation: badgeAppear 1s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
        }
      `}</style>

      <div className="flex flex-col items-center justify-center min-h-[50vh] sm:min-h-[60vh] relative">
        {/* Vortex de particules */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          {particles.map((particle) => (
            <div
              key={particle.id}
              className="vortex-particle absolute rounded-full bg-tessan-yellow"
              style={{
                '--start-angle': `${particle.angle}deg`,
                '--distance': `${particle.distance}px`,
                '--speed': `${particle.speed}s`,
                width: `${particle.size}px`,
                height: `${particle.size}px`,
                boxShadow: '0 0 10px rgba(255, 241, 152, 0.5)'
              } as React.CSSProperties}
            />
          ))}
        </div>

        {/* Badge central */}
        <div className={`relative z-10 ${showBadge ? 'badge-appear' : 'opacity-0'}`}>
          <div className="w-32 h-32 sm:w-44 sm:h-44 bg-tessan-cream rounded-2xl flex items-center justify-center shadow-2xl shadow-tessan-yellow/50">
            <span className="text-6xl sm:text-8xl">{badge.emoji}</span>
          </div>
        </div>

        <div className={`mt-8 text-center transition-all duration-700 delay-500 ${showBadge ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
          <div className="text-2xl sm:text-4xl font-black text-tessan-yellow">{badge.name}</div>
          <div className="text-lg sm:text-xl text-tessan-cream mt-2">+{badge.points_awarded} points</div>
          <div className="text-tessan-cream/70 mt-4 max-w-[280px] sm:max-w-xs">{badge.description}</div>
        </div>
      </div>
    </AnimationLayout>
  )
}
